/**
 * Admin → Security page (Iter 57 · Palantir-deep edition)
 * ───────────────────────────────────────────────────────
 * Operator console for field-level encryption + KMS wiring.
 *
 * Visual language: Bloomberg Terminal / Palantir Gotham
 *   - Sharp 1px borders
 *   - Monospace data readouts for every count + status badge
 *   - Bracketed section headers ([FIELD ENC], [KMS], [BY COLLECTION])
 *   - ASCII-style fill bars for encrypted/plaintext ratios
 *   - Dotted hairline dividers between data rows
 */
import React, { useEffect, useState } from 'react';
import { Shield, Lock, KeyRound, AlertTriangle, CheckCircle2, RefreshCw, ArrowRight, Link as LinkIcon } from 'lucide-react';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';
import BruteForceWidget from '@/components/BruteForceWidget';
import EmailDeliveryPanel from '@/pages/admin/panels/EmailDeliveryPanel';

export default function AdminSecurity() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [form, setForm] = useState({ provider: 'none', key_id_or_arn: '', region: '', notes: '' });

  // Audit-chain integrity panel state
  const [chain, setChain] = useState(null);
  const [chainBusy, setChainBusy] = useState(false);
  const [chainAction, setChainAction] = useState('idle'); // 'verifying' | 'reanchoring'

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches. */
  const refresh = () => {
    setLoading(true);
    api.get('/admin/security/status')
      .then(({ data }) => {
        setStatus(data);
        setForm({
          provider: data.kms?.provider || 'none',
          key_id_or_arn: data.kms?.key_id_or_arn || '',
          region: data.kms?.region || '',
          notes: data.kms?.notes || '',
        });
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load security status'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);
  /* eslint-enable */

  const saveKms = async () => {
    try {
      await api.put('/admin/security/kms', form);
      toast.success('KMS configuration saved (pending wire on next deploy).');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save KMS config');
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post('/admin/security/migrate-plaintext');
      toast.success(`Encrypted ${data.migrated.total} field${data.migrated.total === 1 ? '' : 's'}.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  /* ── Audit-chain handlers ──────────────────────────────────────────── */
  const verifyChain = async () => {
    setChainBusy(true);
    setChainAction('verifying');
    try {
      const { data } = await api.get('/security/audit/verify');
      setChain({ kind: 'verify', ...data });
      if (data.ok) toast.success(`Audit chain clean — ${data.total} entries verified.`);
      else toast.error(`Audit chain broken at #${data.broken_at}: ${data.reason}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify chain');
    } finally {
      setChainBusy(false);
      setChainAction('idle');
    }
  };

  const reanchorChain = async () => {
    if (!window.confirm(
      'Re-anchor the audit chain?\n\n'
      + 'This walks every tenant\'s audit log, recomputes the cryptographic '
      + 'hashes end-to-end, and appends a re-anchor marker so the maintenance '
      + 'is itself logged. No rows are deleted.\n\n'
      + 'Use this only when the chain is broken by direct DB edits or aborted migrations.'
    )) return;
    setChainBusy(true);
    setChainAction('reanchoring');
    try {
      const { data } = await api.post('/security/audit/reanchor');
      setChain({ kind: 'reanchor', ...data });
      const totalRows = data.tenants.reduce((a, t) => a + t.rebuilt, 0);
      const allOk = data.verified.every((v) => v.ok);
      if (allOk) toast.success(`Chain re-anchored — ${totalRows} entries across ${data.tenants.length} tenant${data.tenants.length === 1 ? '' : 's'}, all verify clean.`);
      else toast.error('Re-anchor completed but verification still reports issues. See panel below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-anchor failed');
    } finally {
      setChainBusy(false);
      setChainAction('idle');
    }
  };

  // Compute totals + dial-percentage for the global encryption-ratio bar.
  const totals = status?.field_encryption || { total_encrypted: 0, total_plaintext: 0, by_collection: {}, envelope_prefix: 'ENC1::' };
  const total = totals.total_encrypted + totals.total_plaintext;
  const ratioEnc = total > 0 ? (totals.total_encrypted / total) * 100 : 100;

  return (
    <div className="space-y-5" data-testid="admin-security-page">
      {/* ── Header ── */}
      <div className="pal-card pal-grid-bg p-5 flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.40)',
          }}
        >
          <Shield size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[ADMIN · SECURITY]</div>
          <h1 className={`display text-2xl mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
            Encryption at Rest &amp; In Transit
          </h1>
          <p className="text-sm mt-1 max-w-2xl opacity-70">
            Sensitive credentials are stored with AES-256-GCM authenticated encryption,
            keyed per-tenant via HKDF-SHA256. Wire a Customer Master Key below to
            replace the local fallback root-of-trust.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          data-testid="admin-security-refresh"
          className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold ${
            isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          } disabled:opacity-50 flex items-center gap-1.5`}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          REFRESH
        </button>
      </div>

      {/* ── Email Delivery Diagnostic (extracted) ── */}
      <EmailDeliveryPanel isDark={isDark} />

      {/* ── Field Encryption Status ── */}
      <div className="pal-card pal-card-live pal-grid-bg p-5" data-testid="admin-security-field-status">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[FIELD ENCRYPTION]</span>
          </div>
          {total > 0 && (
            <span className={`pal-status ${totals.total_plaintext === 0 ? 'pal-status-ok' : 'pal-status-warn'}`}>
              {totals.total_plaintext === 0 ? 'ALL ENC' : `${totals.total_plaintext} PLAINTEXT`}
            </span>
          )}
        </div>

        {!status ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : (
          <>
            {/* Top-level data readout */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <DataReadout label="ENCRYPTED" value={totals.total_encrypted} accent="emerald" isDark={isDark} />
              <DataReadout label="PLAINTEXT" value={totals.total_plaintext} accent={totals.total_plaintext > 0 ? 'rose' : 'slate'} isDark={isDark} />
              <DataReadout label="ALGORITHM" value="AES-256-GCM" accent="slate" isDark={isDark} small />
              <DataReadout label="ENVELOPE" value={totals.envelope_prefix} accent="slate" isDark={isDark} small />
            </div>

            {/* Global encryption ratio bar */}
            {total > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="pal-bracket text-[9px]">[GLOBAL · ENC RATIO]</span>
                  <span className="pal-mono text-[11px] tabular-nums opacity-80">
                    {ratioEnc.toFixed(1)}% · {totals.total_encrypted}/{total}
                  </span>
                </div>
                <AsciiBar value={ratioEnc} cells={48} />
              </div>
            )}

            {/* By-collection rows — Bloomberg-style data table */}
            <div className="pal-bracket text-[9px] mb-2">[BY COLLECTION]</div>
            <div className="rounded-md border border-[rgba(174,82,234,0.18)] overflow-hidden">
              {Object.entries(totals.by_collection || {}).map(([coll, info], i, arr) => {
                const sum = info.encrypted + info.plaintext;
                const pct = sum > 0 ? (info.encrypted / sum) * 100 : 100;
                const isLast = i === arr.length - 1;
                return (
                  <div
                    key={coll}
                    className={`flex items-center gap-3 px-3 py-2.5 ${
                      isDark ? 'bg-[rgba(174,82,234,0.03)]' : 'bg-slate-50'
                    } ${!isLast ? 'border-b border-dashed border-[rgba(174,82,234,0.15)]' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`pal-mono text-[12px] truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                        {coll}
                      </div>
                      <div className="pal-mono text-[9px] mt-0.5 opacity-55 truncate">
                        {info.fields.join(' · ')}
                      </div>
                    </div>
                    <div className="hidden md:block flex-1 min-w-[120px]">
                      <AsciiBar value={pct} cells={20} />
                    </div>
                    <span className="pal-mono text-[10px] tabular-nums w-12 text-right opacity-80">
                      {pct.toFixed(0)}%
                    </span>
                    <span className="pal-status pal-status-ok">
                      {String(info.encrypted).padStart(2, '0')}E
                    </span>
                    {info.plaintext > 0 ? (
                      <span className="pal-status pal-status-alert">
                        {String(info.plaintext).padStart(2, '0')}P
                      </span>
                    ) : (
                      <span className="pal-status pal-status-ok">00P</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Migration CTA / All-clear */}
            {totals.total_plaintext > 0 && (
              <button
                onClick={runMigration}
                disabled={migrating}
                data-testid="admin-security-migrate"
                className={`mt-5 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded flex items-center gap-2 pal-mono transition-transform hover:translate-y-[-1px] ${
                  isDark ? 'bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]' : 'bg-[var(--ve-violet-shadow)] text-white hover:bg-[var(--ve-violet)]'
                } disabled:opacity-50`}
              >
                {migrating ? <RefreshCw size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                {migrating ? 'ENCRYPTING…' : `ENCRYPT ${totals.total_plaintext} PLAINTEXT FIELD${totals.total_plaintext === 1 ? '' : 'S'} →`}
              </button>
            )}
            {totals.total_plaintext === 0 && totals.total_encrypted > 0 && (
              <div className={`mt-5 flex items-center gap-2 pal-mono text-[11px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                <CheckCircle2 size={14} />
                ALL SENSITIVE FIELDS ENCRYPTED AT REST · STATUS = OK
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Active Throttles · Brute-Force Shield ── */}
      <BruteForceWidget isDark={isDark} />

      {/* ── Audit-chain integrity · verify + re-anchor ── */}
      <div className="pal-card pal-grid-bg p-5" data-testid="admin-security-audit-chain">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <LinkIcon size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[AUDIT CHAIN · TAMPER-EVIDENT LOG]</span>
          </div>
          {chain?.kind === 'verify' && chain.ok && (
            <span className="pal-status pal-status-ok">CHAIN OK · {chain.total} ENTRIES</span>
          )}
          {chain?.kind === 'verify' && !chain.ok && (
            <span className="pal-status pal-status-alert">BROKEN @ #{chain.broken_at}</span>
          )}
          {chain?.kind === 'reanchor' && (
            <span className={`pal-status ${chain.ok ? 'pal-status-ok' : 'pal-status-alert'}`}>
              {chain.ok ? `RE-ANCHORED · ${chain.tenants.length} TENANT${chain.tenants.length === 1 ? '' : 'S'}` : 'RE-ANCHOR FAILED'}
            </span>
          )}
        </div>

        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'} mb-4 leading-relaxed max-w-3xl`}>
          Every <code className="pal-mono">audit_logs</code> entry carries a SHA-256 hash of its predecessor, so any
          modification, deletion or insertion is mathematically detectable. <strong>Verify</strong> walks the chain
          end-to-end and reports the first break. <strong>Re-anchor</strong> recomputes every hash in tenant&nbsp;order
          and appends an <code className="pal-mono">audit_chain_reanchored</code> marker — use it only when the chain
          was broken by direct DB edits during development. No rows are deleted; only the chain metadata is
          rewritten.
        </p>

        {chain?.kind === 'reanchor' && (
          <div className={`mb-4 rounded border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200 bg-slate-50'} divide-y ${isDark ? 'divide-white/[0.06]' : 'divide-slate-200'}`}>
            {chain.verified.map((v) => (
              <div key={v.tenant_id} className="flex items-center justify-between px-3 py-2 pal-mono text-[11px]" data-testid={`reanchor-row-${v.tenant_id}`}>
                <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>{v.tenant_id}</span>
                <span className="flex items-center gap-3">
                  <span className={`tabular-nums ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{String(v.total).padStart(4, '0')} entries</span>
                  <span className={`pal-status ${v.ok ? 'pal-status-ok' : 'pal-status-alert'}`}>{v.ok ? 'OK' : 'BROKEN'}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={verifyChain}
            disabled={chainBusy}
            data-testid="admin-security-verify-chain"
            className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded flex items-center gap-2 pal-mono border transition-transform hover:translate-y-[-1px] ${
              isDark ? 'border-white/15 text-slate-200 hover:bg-white/5' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            } disabled:opacity-50`}
          >
            {chainAction === 'verifying' ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {chainAction === 'verifying' ? 'VERIFYING…' : 'VERIFY CHAIN'}
          </button>
          <button
            onClick={reanchorChain}
            disabled={chainBusy}
            data-testid="admin-security-reanchor-chain"
            className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded flex items-center gap-2 pal-mono transition-transform hover:translate-y-[-1px] ${
              isDark ? 'bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]' : 'bg-[var(--ve-violet-shadow)] text-white hover:bg-[var(--ve-violet)]'
            } disabled:opacity-50`}
          >
            {chainAction === 'reanchoring' ? <RefreshCw size={12} className="animate-spin" /> : <LinkIcon size={12} />}
            {chainAction === 'reanchoring' ? 'RE-ANCHORING…' : 'RE-ANCHOR CHAIN →'}
          </button>
        </div>
      </div>

      {/* ── KMS Configuration ── */}
      <div className="pal-card pal-grid-bg p-5" data-testid="admin-security-kms">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <KeyRound size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[KMS · CUSTOMER MASTER KEY]</span>
          </div>
          {status?.kms?.status && (
            <span className={`pal-status ${
              status.kms.status === 'pending_wire' ? 'pal-status-warn'
              : status.kms.status === 'configured'  ? 'pal-status-ok'
              : 'pal-status-violet'
            }`}>
              {String(status.kms.status).replace('_', ' ').toUpperCase()}
            </span>
          )}
        </div>
        <p className="text-xs mb-4 opacity-70 leading-relaxed">
          Replace the local fallback KEK with a cloud KMS-managed Customer Master Key.
          The KMS reference is stored here now and unwrapped at backend boot on the next deploy.
        </p>

        {status?.kms?.status === 'pending_wire' && (
          <div className={`mb-4 p-3 rounded border flex items-start gap-2 ${
            isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              KMS reference saved. Field encryption is currently using the local fallback KEK
              until the next backend deploy wires the CMK material into the <code className="pal-mono">VE_FIELD_ENC_KEY</code> env var.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LabelledSelect
            label="PROVIDER"
            value={form.provider}
            onChange={(v) => setForm((f) => ({ ...f, provider: v }))}
            options={[
              { v: 'none',  l: '— No KMS (local fallback)' },
              { v: 'aws',   l: 'AWS KMS' },
              { v: 'gcp',   l: 'Google Cloud KMS' },
              { v: 'azure', l: 'Azure Key Vault' },
              { v: 'atlas', l: 'Mongo Atlas (auto-managed)' },
            ]}
            isDark={isDark}
            testId="kms-provider"
          />
          <LabelledInput
            label="REGION"
            value={form.region}
            placeholder="me-central-1 / europe-west1 / …"
            onChange={(v) => setForm((f) => ({ ...f, region: v }))}
            isDark={isDark}
            testId="kms-region"
          />
          <LabelledInput
            label="KEY ID / ARN"
            value={form.key_id_or_arn}
            placeholder="arn:aws:kms:me-central-1:123:key/abc-…"
            onChange={(v) => setForm((f) => ({ ...f, key_id_or_arn: v }))}
            isDark={isDark}
            testId="kms-key-arn"
            wide
          />
          <LabelledInput
            label="NOTES (OPTIONAL)"
            value={form.notes}
            placeholder="Owner / rotation policy / etc."
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            isDark={isDark}
            testId="kms-notes"
            wide
          />
        </div>

        <button
          onClick={saveKms}
          data-testid="admin-security-save-kms"
          className={`mt-4 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono transition-transform hover:translate-y-[-1px] ${
            isDark ? 'bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]' : 'bg-[var(--ve-violet-shadow)] text-white hover:bg-[var(--ve-violet)]'
          }`}
        >
          SAVE KMS CONFIGURATION →
        </button>
      </div>
    </div>
  );
}

/* ── Bloomberg-style data readout ─────────────────────────────────────── */
function DataReadout({ label, value, accent, isDark, small }) {
  const accents = {
    emerald: isDark
      ? { fg: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' }
      : { fg: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
    rose: isDark
      ? { fg: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-500/30' }
      : { fg: 'text-rose-700',    bg: 'bg-rose-50 border-rose-200' },
    slate: isDark
      ? { fg: 'text-slate-200',   bg: 'bg-white/[0.03] border-white/10' }
      : { fg: 'text-slate-700',   bg: 'bg-slate-50 border-slate-200' },
  };
  const a = accents[accent] || accents.slate;
  return (
    <div className={`p-3 rounded-md border ${a.bg}`}>
      <div className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 mb-1">{label}</div>
      <div className={`pal-mono ${small ? 'text-sm' : 'text-2xl'} tabular-nums font-bold ${a.fg}`}>
        {value}
      </div>
    </div>
  );
}

/* ── ASCII-block progress bar ─────────────────────────────────────────── */
function AsciiBar({ value, cells = 16 }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const filled = Math.round((v / 100) * cells);
  return (
    <div className="pal-progress" aria-hidden>
      <span className="pal-fill">{'█'.repeat(filled)}</span>
      <span className="pal-empty">{'░'.repeat(cells - filled)}</span>
    </div>
  );
}

/* ── Form inputs (kept minimal, monospace-labelled) ───────────────────── */
function LabelledInput({ label, value, onChange, placeholder, isDark, testId, wide }) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="pal-mono text-[9px] uppercase tracking-[0.20em] block mb-1 opacity-70">
        {label}
      </label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-full px-3 py-2 text-sm rounded border bg-transparent pal-mono ${
          isDark
            ? 'border-white/10 text-white placeholder-slate-500 focus:border-[var(--ve-violet-bright)]'
            : 'border-slate-300 text-[#0A0A0A] placeholder-slate-400 focus:border-[var(--ve-violet-shadow)]'
        } focus:outline-none`}
      />
    </div>
  );
}

function LabelledSelect({ label, value, onChange, options, isDark, testId }) {
  return (
    <div>
      <label className="pal-mono text-[9px] uppercase tracking-[0.20em] block mb-1 opacity-70">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-full px-3 py-2 text-sm rounded border pal-mono ${
          isDark ? 'bg-[#0A0510] border-white/10 text-white' : 'bg-white border-slate-300 text-[#0A0A0A]'
        } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
